
import { Property, DealType } from '../types';

/**
 * PRODUCTION WORKER CONFIGURATION
 * All sensitive data and GHL proxying is handled server-side via Cloudflare Workers.
 */
const WORKER_API_BASE = "https://assets.sitefindr.co.uk/api";
const DEALS_ENDPOINT = `${WORKER_API_BASE}/ghl/deals`;
const ASSET_BASE_URL = `${WORKER_API_BASE}/deal-assets`;

/**
 * Fetches image URLs from the Asset Discovery Worker
 */
export const fetchImagesForDeal = async (dealRef: string): Promise<string[]> => {
  if (!dealRef || dealRef === 'REF-TBC') return [];
  try {
    const response = await fetch(`${ASSET_BASE_URL}?deal_ref=${encodeURIComponent(dealRef)}`);
    if (!response.ok) return [];
    const images = await response.json();
    return Array.isArray(images) ? images : [];
  } catch (err) {
    console.error(`Image Discovery failed for ${dealRef}:`, err);
    return [];
  }
};

// Added missing export to resolve AdminPage.tsx error
/**
 * Generates presigned URLs for uploading files to R2 via the worker
 */
export const getPresignedUrls = async (dealRef: string, files: any[]): Promise<{ urls: string[] }> => {
  const response = await fetch(`${ASSET_BASE_URL}/presign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ deal_ref: dealRef, files })
  });
  if (!response.ok) throw new Error(`Presign Error: ${response.status}`);
  return response.json();
};

// Added missing export to resolve AdminPage.tsx error
/**
 * Uploads a file to R2 using a presigned URL with progress tracking
 */
export const uploadFileToR2 = (url: string, file: File, onProgress: (pct: number) => void): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed: ${xhr.status}`));
    };

    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.send(file);
  });
};

/**
 * MAPPING WORKER RECORDS TO INVESTOR-SAFE PROPERTY OBJECTS
 */
const mapWorkerRecordToProperty = (record: any): Property => {
  const price = Number(record.deal_price) || 0;
  const rentPcm = Number(record.rent_pcm) || 0;

  const workerYield = Number(record.gross_yield);
  const yieldPercent = Number.isFinite(workerYield) && workerYield > 0
    ? workerYield
    : (price > 0 ? (rentPcm * 12 / price) * 100 : 0);

  const workerRoi = Number(record.roi);
  const roiPercent = Number.isFinite(workerRoi) ? workerRoi : 0;

  const rawStrategy = (record.property_strategy || '').toUpperCase();
  let mappedType: DealType = 'Buy to Let';
  if (rawStrategy.includes('HMO')) mappedType = 'HMO';
  else if (rawStrategy.includes('BRRR')) mappedType = 'BRRR';
  else if (rawStrategy.includes('FLIP')) mappedType = 'Flip';
  else if (rawStrategy.includes('R2R')) mappedType = 'R2R';

  return {
    id: record.property_reference_id || Math.random().toString(36).substr(2, 9),
    listingRef: record.property_reference_id || 'REF-TBC',
    town: record.town_city || 'UK City',
    postcodeOutcode: record.postcode_outward || 'UK',
    address: "Location provided on request",
    hook: record.headline_title || `${record.bedrooms || 0} Bed Property`,
    description: record.investment_overview || 'Details provided on request.',
    dealType: mappedType,
    price: price,
    marketValue: Number(record.end_value) || 0,
    bedrooms: Number(record.bedrooms) || 0,
    bathrooms: Number(record.bathrooms) || 0,
    yieldPercent: Number(yieldPercent.toFixed(1)),
    roiPercent: Number(roiPercent.toFixed(1))
  };
};

export const fetchInvestmentOpportunities = async (): Promise<Property[]> => {
  try {
    const response = await fetch(`${DEALS_ENDPOINT}?page=1&pageLimit=100`);
    if (!response.ok) throw new Error(`Worker Error: ${response.status}`);
    const data = await response.json();
    return (data.records || []).map(mapWorkerRecordToProperty);
  } catch (err) {
    console.warn("Worker unavailable, falling back to mock data");
    return [];
  }
};

export const fetchPropertyById = async (id: string): Promise<Property | null> => {
  const allProperties = await fetchInvestmentOpportunities();
  return allProperties.find(p => p.id === id || p.listingRef === id) || null;
};
